<template>
  <div qaid="warranty-details-grid" class="my-6">
    <div class="flex flex-col md:flex-row justify-between">
      <p>
        <span qaid="warranty-details-order-number" class="font-medium" v-html="`${bp('orderNumber')}: `" />
        <pdl-link
          standalone
          content="warrantyDetails.code"
          qaid="warranty-order-link"
          :href="
            `${encodedContextPath}/my-account/order/${warrantyDetails.code}?company=${warrantyDetails.details.orderCompany}&ordertype=${warrantyDetails.details.orderType}`
          "
        >
          {{ warrantyDetails.code }}
        </pdl-link>
      </p>
      <div v-if="Array.isArray(consignment.invoices) && consignment.invoices.length">
        <p v-for="invoice in consignment.invoices" :key="invoice.invoiceNumber">
          <span qaid="warranty-details-grid-invoice" class="font-medium" v-html="`${bp('invoiceNumber')}: `" />
          <pdl-link standalone qaid="warranty-invoice-download" @click="downloadInvoice(invoice.invoiceUrl)">
            {{ invoice.invoiceNumber }}
          </pdl-link>
        </p>
      </div>
    </div>
    <table class="b2b-grid b2b-grid--collapse is-compact table-fixed" qaid="warranty-details-order-grid">
      <thead class="b2b-grid__header">
        <tr>
          <th
            v-for="item in filterHeaders()"
            :key="item"
            :qaid="`warranty-details-grid-header-${item}`"
            :class="[
              'b2b-grid__cell',
              'b2b-grid__head',
              {'lg:w-1/4': item === 'description'},
              {'lg:w-1/6': item === 'status'},
            ]"
          >
            <span v-html="bp(item)" />
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="entry in warrantyDetails.entries" :key="entry.id">
          <td qaid="warranty-details-grid-sku" class="b2b-grid__cell lg:table-cell">
            <div class="b2b-grid__label" v-html="bp('itemNumber')" />
            <div class="b2b-grid__content">
              <pdl-link standalone qaid="pdp-sku-link" :href="`${encodedContextPath}/p/${entry.sku}`">
                <grid-text
                  :data="{
                    value: entry.sku,
                  }"
                />
              </pdl-link>
            </div>
          </td>

          <td qaid="warranty-details-grid-quantity" class="b2b-grid__cell lg:table-cell">
            <div class="b2b-grid__label" v-html="bp('quantity')" />
            <div class="b2b-grid__content">
              <grid-text
                :data="{
                  value: entry.quantityOrdered,
                }"
              />
            </div>
          </td>

          <td qaid="warranty-details-grid-description" class="b2b-grid__cell lg:table-cell">
            <div class="b2b-grid__label" v-html="bp('description')" />
            <div v-if="entry.itemName" class="b2b-grid__content">
              <grid-text
                :data="{
                  value: entry.itemName,
                }"
              />
            </div>
          </td>

          <td qaid="warranty-details-grid-status" class="b2b-grid__cell lg:table-cell">
            <div class="b2b-grid__label" v-html="bp('status')" />
            <div class="b2b-grid__content">
              <div>
                <pdl-label :kind="calculateLabelKind(entry.lineStatus)">
                  <span v-html="bp(`${entry.lineStatus}`)" />
                </pdl-label>
              </div>
            </div>
          </td>

          <td qaid="warranty-details-grid-upc" class="b2b-grid__cell lg:table-cell">
            <div class="b2b-grid__label" v-html="bp('UPCNumber')" />
            <div v-if="entry.upc" class="b2b-grid__content">
              <grid-text
                :data="{
                  value: entry.upc,
                }"
              />
            </div>
          </td>

          <td qaid="warranty-details-grid-warehouse" class="b2b-grid__cell lg:table-cell">
            <div class="b2b-grid__label" v-html="bp('warehouse')" />
            <div class="b2b-grid__content">
              <grid-text
                :data="{
                  value: entry.warehouse,
                }"
              />
            </div>
          </td>

          <td v-if="!consumerFriendlyMode" qaid="warranty-details-grid-unit-price" class="b2b-grid__cell lg:table-cell">
            <div class="b2b-grid__label" v-html="bp('unitPrice')" />
            <div class="b2b-grid__content">
              <grid-text
                :data="{
                  value: entry.basePrice.formattedValue,
                }"
              />
            </div>
          </td>

          <td
            v-if="!consumerFriendlyMode"
            qaid="warranty-details-grid-extended-price"
            class="b2b-grid__cell lg:table-cell"
          >
            <div class="b2b-grid__label" v-html="bp('extendedPrice')" />
            <div class="b2b-grid__content">
              <grid-text
                :data="{
                  value: entry.totalPrice.formattedValue,
                }"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex';
import GridText from '@/components/grid/GridText';
import {PdlLink} from '@pedal/pdl-link';
import {PdlLabel} from '@pedal/pdl-label';

export default {
  components: {GridText, PdlLink, PdlLabel},

  props: {
    warrantyDetails: {
      type: Object,
      required: true,
    },
    consignment: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      colHeaders: [
        'itemNumber',
        'quantity',
        'description',
        'status',
        'UPCNumber',
        'warehouse',
        'unitPrice',
        'extendedPrice',
      ],
    };
  },
  computed: {
    ...mapState('backend', ['encodedContextPath']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },
  methods: {
    downloadInvoice(invoiceUrl) {
      window.location.href = `${invoiceUrl}`;
    },
    filterHeaders() {
      if (this.consumerFriendlyMode) {
        return this.colHeaders.filter((header) => {
          return header !== 'unitPrice' && header !== 'extendedPrice';
        });
      } else {
        return this.colHeaders;
      }
    },
    calculateLabelKind(status) {
      let labelKind;

      switch (status) {
        case 'INVOICED':
          labelKind = 'prominent';
          break;
        case 'IN_WAREHOUSE':
          labelKind = 'important';
          break;
        case 'PROCESSING':
          labelKind = 'important';
          break;
        case 'PLACED':
          labelKind = 'default';
          break;
        case 'CANCELLED':
          labelKind = 'critical';
          break;
        case 'SHIPPED':
          labelKind = 'positive';
          break;
        case 'AWAITING_RETURN':
          labelKind = 'default';
          break;
      }
      return labelKind;
    },
  },
};
</script>
